import { Build, ExpandLess, ExpandMore, Face, Favorite, Notifications, Publish } from "@mui/icons-material";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import DescriptionIcon from "@mui/icons-material/Description";
import GroupIcon from "@mui/icons-material/Group";
import IosShareIcon from "@mui/icons-material/IosShare";
import ListIcon from "@mui/icons-material/List";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import {
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListItemButton,
    ListSubheader,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Box } from "@mui/system";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import LogoImg from "../../assets/SquareLogo.png";
import ComingSoonFeature from "../../components/ComingSoonFeature/ComingSoonFeature";
import DisplayText from "../../components/DisplayText/DisplayText";
import { nebulaClient2 } from "../../lib/nebulaClient";
import { DataError } from "../../shared/DataError";
import { getSelectedTrial } from "../auth/login/loginSlice";
import usePermissions from "../permissions/usePermissions";
import useRoles from "../permissions/useRoles";
import { DesktopDatePicker } from "@mui/lab";

interface IMenuProps {
    onClose: (e: React.MouseEvent) => void;
    onExportClick: () => void;
}

enum billingReportType {
    IMAGES_EVENTS = "IMAGES_EVENTS",
    CRF_EVENTS = "CRF_EVENTS",
    USERS_ADDED = "USERS_ADDED",
}

const BILLING_REPORT_DATE_STORAGE_KEY = "nebula.billingReportDateRange";
const BILLING_REPORT_FORMAT_STORAGE_KEY = "nebula.billingReportFormat";

type BillingReportDateRange = {
    from: string;
    to: string;
};

const Menu: React.FC<IMenuProps> = ({ onClose, onExportClick: handleExportCustomFormsData }) => {
    const { hasPermissions } = usePermissions();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { userHasRole } = useRoles();

    const selectedTrial = useAppSelector(getSelectedTrial);
    const hasManual = useAppSelector((state) => state.userInfo.trialHasManual);
    const [openExportCRF, setOpenExportCRF] = useState(false);
    const [openBillingReport, setOpenBillingReport] = useState(false);
    const [openCompletionCensus, setOpenCompletionCensus] = useState(false);
    const [openModalBillingReport, setOpenModalBillingReport] = useState(false);
    const [openModalUsersReport, setOpenModalUsersReport] = useState(false);
    const [openModalAllUsersReport, setOpenModalAllUsersReport] = useState(false);
    const [openModalBasicAuditReport, setOpenModalBasicAuditReport] = useState(false);
    const [billingReport, setBillingReport] = useState<billingReportType | null>(null);
    const [CompletionCensusDate, setCompletionCensusDate] = useState({ from: "", to: "" });
    const [basicAuditReportDate, setBasicAuditReportDate] = useState({ from: "", to: "" });

    const getCurrentMonthDates = () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
            from: firstDay.toISOString(),
            to: lastDay.toISOString(),
        };
    };

    const getInitialBillingReportDate = (): BillingReportDateRange => {
        const currentMonthDates = getCurrentMonthDates();

        if (typeof window === "undefined") {
            return currentMonthDates;
        }

        try {
            const storedDateRange = window.sessionStorage.getItem(BILLING_REPORT_DATE_STORAGE_KEY);

            if (!storedDateRange) {
                return currentMonthDates;
            }

            const parsedDateRange = JSON.parse(storedDateRange) as Partial<BillingReportDateRange>;

            if (typeof parsedDateRange.from === "string" && typeof parsedDateRange.to === "string") {
                return {
                    from: parsedDateRange.from,
                    to: parsedDateRange.to,
                };
            }

            return currentMonthDates;
        } catch {
            return currentMonthDates;
        }
    };

    const getInitialBillingReportFormat = (): "PDF" | "CSV" => {
        if (typeof window === "undefined") {
            return "PDF";
        }

        const storedBillingReportFormat = window.sessionStorage.getItem(BILLING_REPORT_FORMAT_STORAGE_KEY);

        if (storedBillingReportFormat === "PDF" || storedBillingReportFormat === "CSV") {
            return storedBillingReportFormat;
        }

        return "PDF";
    };

    const [billingReportDate, setBillingReportDate] = useState<BillingReportDateRange>(getInitialBillingReportDate);
    const [billingReportFormat, setBillingReportFormat] = useState<"PDF" | "CSV">(getInitialBillingReportFormat);
    const [usersReportFormat, setUsersReportFormat] = useState<"PDF" | "CSV">("PDF");
    const [allUsersReportFormat, setAllUsersReportFormat] = useState<"PDF" | "CSV">("CSV");

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        window.sessionStorage.setItem(BILLING_REPORT_DATE_STORAGE_KEY, JSON.stringify(billingReportDate));
    }, [billingReportDate]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        window.sessionStorage.setItem(BILLING_REPORT_FORMAT_STORAGE_KEY, billingReportFormat);
    }, [billingReportFormat]);

    function handleCloseCompletionCensus(): void {
        setOpenCompletionCensus(false);
        onClose({} as React.MouseEvent);
    }
    const handleChangeCompletionCensusDate = (newDate: Date | null, type: "from" | "to") => {
        if (newDate) {
            setCompletionCensusDate((prev) => ({
                ...prev,
                [type]: newDate.toISOString(),
            }));
        }
    };

    const handleCollapseExportMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpenExportCRF(!openExportCRF);
    };

    const handleCollapseBillingReport = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpenBillingReport(!openBillingReport);
    };
    function handleCloseBillingReport(): void {
        setOpenModalBillingReport(false);
        onClose({} as React.MouseEvent);
    }

    function handleCloseUsersReport(): void {
        setOpenModalUsersReport(false);
        onClose({} as React.MouseEvent);
    }

    function handleCloseAllUsersReport(): void {
        setOpenModalAllUsersReport(false);
        onClose({} as React.MouseEvent);
    }

    function handleCloseBasicAuditReport(): void {
        setOpenModalBasicAuditReport(false);
        onClose({} as React.MouseEvent);
    }

    const handleChangeBasicAuditReportDate = (newDate: Date | null, type: "from" | "to") => {
        setBasicAuditReportDate((prev) => ({
            ...prev,
            [type]: newDate ? newDate.toISOString() : "",
        }));
    };

    const getStatusCodeFromError = (error?: DataError): number | undefined => {
        if (!error) return undefined;

        if (error.kind === "ApiError") {
            return error.statusCode;
        }

        if (error.kind === "Unauthorized") {
            return 401;
        }

        return undefined;
    };

    const getDownloadErrorMessage = (statusCode?: number): string => {
        if (statusCode === 403) {
            return "You are not authorized to download All Users Report.";
        }

        if (statusCode === 400) {
            return "Invalid report format. Please select CSV or PDF.";
        }

        if (statusCode === 401) {
            return "Your session has expired. Please sign in again.";
        }

        if (statusCode === 500) {
            return "Server error while generating All Users Report. Please try again.";
        }

        return "Something went wrong while downloading All Users Report.";
    };

    const getDownloadFileName = (contentDisposition: string | undefined, fallbackName: string) => {
        if (!contentDisposition) {
            return fallbackName;
        }

        const fileNameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (fileNameStarMatch?.[1]) {
            return decodeURIComponent(fileNameStarMatch[1]).replace(/['"]/g, "");
        }

        const fileNameMatch = contentDisposition.match(/filename=([^;]+)/i);
        if (fileNameMatch?.[1]) {
            return fileNameMatch[1].trim().replace(/['"]/g, "");
        }

        return fallbackName;
    };

    const downloadFileBlob = (blob: Blob, fileName: string) => {
        const url = window.URL.createObjectURL(blob);

        // Crete link element
        const link = document.createElement("a");
        link.href = url;

        // get trial name from redux store
        link.setAttribute("download", fileName);

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link!.parentNode!.removeChild(link);
    };

    const handleExportCensus = async () => {
        if (!selectedTrial) return;

        enqueueSnackbar("Generating Census report...", { variant: "info" });

        try {
            const fileData = await nebulaClient2.get(`report/${selectedTrial.uuid}/census?filterColumns=fileName`);

            const fileName = `${selectedTrial.name}_Census_${new Date().toISOString()}.csv`;

            // @ts-ignore
            downloadFileBlob(new Blob([fileData.data]), fileName);

            closeSnackbar();
        } catch (e) {
            closeSnackbar();
            enqueueSnackbar("Something went wrong", { variant: "error" });
        }
    };

    const handleExportCompletionCensus = async () => {
        if (!selectedTrial) return;

        enqueueSnackbar("Generating Completion Census report...", { variant: "info" });

        try {
            const fileData = await nebulaClient2.get(
                `report/${selectedTrial.uuid}/census-completion${
                    CompletionCensusDate.from.length && CompletionCensusDate.to.length
                        ? `?from=${CompletionCensusDate.from}&to=${CompletionCensusDate.to}`
                        : ""
                }`
            );

            const fileName = `${selectedTrial.name}_Completion_Census_${new Date().toISOString()}.csv`;

            // @ts-ignore
            downloadFileBlob(new Blob([fileData.data]), fileName);

            closeSnackbar();
            setOpenCompletionCensus(false);
            onClose({} as React.MouseEvent);
        } catch (e) {
            closeSnackbar();
            enqueueSnackbar("Something went wrong", { variant: "error" });
        }
    };

    const handleExportCrf = async (e: React.MouseEvent, final: boolean = false) => {
        e.stopPropagation();
        if (!selectedTrial) return;

        enqueueSnackbar("Generating CRF report...", { variant: "info" });

        try {
            const fileData = await nebulaClient2.get(`trial/${selectedTrial.uuid}/crf?final=${final}`);

            const fileName = `${selectedTrial.name}_${final ? "Final" : "All"}_CRF_${new Date().toISOString()}.csv`;

            // @ts-ignore
            downloadFileBlob(new Blob([fileData.data]), fileName);

            closeSnackbar();
        } catch (e) {
            closeSnackbar();
            enqueueSnackbar("Something went wrong", { variant: "error" });
        }
    };

    const handleBillingReports = async (
        e: React.MouseEvent,
        reportType: billingReportType | null,
        from: String,
        to: String,
        format: "PDF" | "CSV"
    ) => {
        e.stopPropagation();
        if (!selectedTrial) return;
        if (!reportType) return;
        if (!from || from === "" || !to || to === "") return;

        setOpenModalBillingReport(false);
        setBillingReport(null);

        enqueueSnackbar("Generating Images Billing report...", { variant: "info" });

        try {
            const fileData = await nebulaClient2.get(
                `report/billing-report/${selectedTrial.uuid}?reportType=${reportType}&from=${from}&to=${to}&format=${format}`,
                {
                    responseType: "arraybuffer",
                }
            );

            downloadFileBlob(
                // @ts-ignore
                new Blob([fileData.data], { type: format === "PDF" ? "application/pdf" : "text/csv" }),
                `${selectedTrial.name}_Billing_Report_${reportType}_${from}_${to}.${format.toLowerCase()}`
            );

            closeSnackbar();
        } catch (e) {
            closeSnackbar();
            enqueueSnackbar("Something went wrong", { variant: "error" });
        }
    };

    const handleModalBillingReport = (e: React.MouseEvent, reportType: billingReportType) => {
        e.stopPropagation();
        setBillingReport(reportType);
        setOpenModalBillingReport(true);
    };

    const handleExportCertificationData = async () => {
        if (!selectedTrial) return;

        enqueueSnackbar("Generating Certification report...", { variant: "info" });

        try {
            const fileData = await nebulaClient2.get(`trial/${selectedTrial.uuid}/crf?certification=true`);

            const fileName = `${selectedTrial.name}_Certification_${new Date().toISOString()}.csv`;

            // @ts-ignore
            downloadFileBlob(new Blob([fileData.data]), fileName);

            closeSnackbar();
        } catch (e) {
            closeSnackbar();
            enqueueSnackbar("Something went wrong", { variant: "error" });
        }
    };

    const handleExportUsersData = async (e: React.MouseEvent, format: "PDF" | "CSV") => {
        e.stopPropagation();
        if (!selectedTrial) return;

        enqueueSnackbar("Generating Users report...", { variant: "info" });

        try {
            const fileData = await nebulaClient2.get(
                `report/users-by-trial-report/${selectedTrial.uuid}?format=${format}`,
                {
                    responseType: "arraybuffer",
                }
            );

            const extension = format.toLowerCase();
            const fileName = `${selectedTrial.name}_Users_${new Date().toISOString()}.${extension}`;
            downloadFileBlob(
                // @ts-ignore
                new Blob([fileData.data], { type: format === "PDF" ? "application/pdf" : "text/csv" }),
                fileName
            );

            closeSnackbar();
            setOpenModalUsersReport(false);
        } catch (e) {
            closeSnackbar();
            setOpenModalUsersReport(false);
            enqueueSnackbar("Something went wrong", { variant: "error" });
        }
    };

    const handleExportAllUsersData = async (e: React.MouseEvent, format: "PDF" | "CSV") => {
        e.stopPropagation();

        enqueueSnackbar("Generating All Users report...", { variant: "info" });

        try {
            const fileData = await nebulaClient2.get(`report/all-users-report?format=${format}`, {
                responseType: "arraybuffer",
            });

            const statusCode = getStatusCodeFromError(fileData.error);

            if (fileData.error || !fileData.data) {
                closeSnackbar();
                setOpenModalAllUsersReport(false);
                enqueueSnackbar(getDownloadErrorMessage(statusCode), { variant: "error" });
                return;
            }

            const headers = fileData.meta?.headers ?? {};
            const contentDisposition = headers["content-disposition"];
            const contentType = headers["content-type"];
            const extension = format === "PDF" ? "pdf" : "csv";
            const fallbackName = `All_Users_Report.${extension}`;
            const fileName = getDownloadFileName(contentDisposition, fallbackName);

            downloadFileBlob(
                // @ts-ignore
                new Blob([fileData.data], { type: contentType ?? (format === "PDF" ? "application/pdf" : "text/csv") }),
                fileName
            );

            closeSnackbar();
            setOpenModalAllUsersReport(false);
        } catch (error) {
            closeSnackbar();
            setOpenModalAllUsersReport(false);
            enqueueSnackbar(getDownloadErrorMessage(), { variant: "error" });
        }
    };

    const handleExportBasicAuditReport = async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!selectedTrial) return;

        enqueueSnackbar("Generating Basic Audit report...", { variant: "info" });

        try {
            const dateParams = [
                basicAuditReportDate.from.length ? `from=${basicAuditReportDate.from}` : "",
                basicAuditReportDate.to.length ? `to=${basicAuditReportDate.to}` : "",
            ]
                .filter(Boolean)
                .join("&");

            const fileData = await nebulaClient2.get(
                `report/basic-audit-report/${selectedTrial.uuid}${dateParams ? `?${dateParams}` : ""}`,
                { responseType: "arraybuffer" }
            );

            if (fileData.error || !fileData.data) {
                closeSnackbar();
                setOpenModalBasicAuditReport(false);
                enqueueSnackbar("Something went wrong while downloading Basic Audit Report.", { variant: "error" });
                return;
            }

            const headers = fileData.meta?.headers ?? {};
            const contentDisposition = headers["content-disposition"];
            const fallbackName = `Basic_Audit_Report_${selectedTrial.name}.csv`;
            const fileName = getDownloadFileName(contentDisposition, fallbackName);

            // @ts-ignore
            downloadFileBlob(new Blob([fileData.data], { type: "text/csv" }), fileName);

            closeSnackbar();
            handleCloseBasicAuditReport();
        } catch (error) {
            closeSnackbar();
            setOpenModalBasicAuditReport(false);
            enqueueSnackbar("Something went wrong while downloading Basic Audit Report.", { variant: "error" });
        }
    };

    const handleDownloadManual = async () => {
        if (!selectedTrial) return;

        enqueueSnackbar("Downloading manual...", { variant: "info" });

        try {
            const fileData = await nebulaClient2.get(`trial/${selectedTrial.uuid}/manual`, {
                responseType: "arraybuffer",
            });

            const fileName = `manual_${selectedTrial.name}.pdf`;
            // @ts-ignore
            const blob = new Blob([fileData.data], { type: "application/pdf" });

            downloadFileBlob(blob, fileName);

            closeSnackbar();
        } catch (e) {
            closeSnackbar();
            enqueueSnackbar("Something went wrong", { variant: "error" });
        }
    };

    return (
        <Box
            sx={{
                width: 300,
                height: "100%",
                backgroundColor: "#2f2f2f",
                display: "flex",
                flexDirection: "column",
            }}
            role="presentation"
        >
            <List
                sx={{ pb: 0 }}
                subheader={
                    <ListSubheader component="div" id="nested-list-subheader">
                        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <Box sx={{ my: 20, display: "flex" }}>
                                <img style={{ maxHeight: 50 }} src={LogoImg} alt="Voxeleron Logo" />
                            </Box>

                            <Box>
                                <DisplayText
                                    type="h5"
                                    variant="regular"
                                    text="iNebula"
                                    style={{ marginBottom: "0px" }}
                                />
                                <DisplayText
                                    type="bodyXSmall"
                                    variant="regular"
                                    text="by Voxeleron"
                                    style={{ marginBottom: "0px" }}
                                />
                            </Box>
                        </Box>
                    </ListSubheader>
                }
            />
            <Divider />
            <List onClick={onClose}>
                <Link
                    to={{
                        pathname: "/patients",
                    }}
                >
                    <ListItem button key={"Review Patients"}>
                        <ListItemIcon>
                            <Face style={{ color: "#fe8c03" }} />
                        </ListItemIcon>
                        <ListItemText primary={"Review Patients"} />
                    </ListItem>
                </Link>
                {hasPermissions(["Upload"]) && (
                    <Link
                        to={{
                            pathname: "/upload",
                        }}
                    >
                        <ListItem button key={"Upload Images"}>
                            <ListItemIcon>
                                <Publish style={{ color: "#fe8c03" }} />
                            </ListItemIcon>
                            <ListItemText primary={"Upload Images"} />
                        </ListItem>
                    </Link>
                )}
                {(userHasRole("Project Manager") || userHasRole("Client Admin") || userHasRole("Master Admin")) && (
                    <>
                        <ListItemButton onClick={handleCollapseExportMenu}>
                            <ListItemIcon>
                                <IosShareIcon style={{ color: "#fe8c03" }} />
                            </ListItemIcon>
                            <ListItemText primary="Export Data" />
                            {openExportCRF ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={openExportCRF} timeout="auto" unmountOnExit>
                            <List component="div" sx={{ pl: 32 }}>
                                <ListItem button onClick={handleExportCrf}>
                                    <ListItemIcon>
                                        <ListIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Full CRF Data" />
                                </ListItem>
                                <ListItem button onClick={(e) => handleExportCrf(e, true)}>
                                    <ListItemIcon>
                                        <PlaylistAddCheckIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Final CRF Data" />
                                </ListItem>
                                <ListItem
                                    disabled={!selectedTrial}
                                    button
                                    onClick={handleExportCertificationData}
                                    key={"Certification Data"}
                                >
                                    <ListItemIcon>
                                        <PhotoCameraIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary={"Certification Data"} />
                                </ListItem>
                                <ListItem
                                    button
                                    disabled={!selectedTrial}
                                    onClick={handleExportCensus}
                                    key={"Export Census"}
                                >
                                    <ListItemIcon>
                                        <GroupIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary={"Census"} />
                                </ListItem>
                                <ListItem
                                    button
                                    disabled={!selectedTrial}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenCompletionCensus(true);
                                    }}
                                    key={"Export Completion Census"}
                                >
                                    <ListItemIcon>
                                        <GroupIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary={"Completion Census"} />
                                </ListItem>
                                <ListItem
                                    disabled={!selectedTrial}
                                    button
                                    onClick={handleExportCustomFormsData}
                                    key={"Custom Form Data"}
                                >
                                    <ListItemIcon>
                                        <DescriptionIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary={"Custom Form Data"} />
                                </ListItem>
                                <ListItem
                                    disabled={!selectedTrial}
                                    button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenModalUsersReport(true);
                                    }}
                                    key={"Users Report"}
                                >
                                    <ListItemIcon>
                                        <GroupIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary={"Users Report"} />
                                </ListItem>
                                {(userHasRole("Client Admin") || userHasRole("Master Admin")) && (
                                    <>
                                        <ListItem
                                            button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenModalAllUsersReport(true);
                                            }}
                                            key={"All Users Report"}
                                        >
                                            <ListItemIcon>
                                                <GroupIcon style={{ color: "#fe8c03" }} />
                                            </ListItemIcon>
                                            <ListItemText primary={"All Users Report"} />
                                        </ListItem>
                                        <ListItem
                                            button
                                            disabled={!selectedTrial}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenModalBasicAuditReport(true);
                                            }}
                                            key={"Basic Audit Report"}
                                        >
                                            <ListItemIcon>
                                                <FactCheckIcon style={{ color: "#fe8c03" }} />
                                            </ListItemIcon>
                                            <ListItemText primary={"Basic Audit Report"} />
                                        </ListItem>
                                    </>
                                )}
                            </List>
                        </Collapse>
                    </>
                )}

                {(userHasRole("Project Manager") || userHasRole("Client Admin") || userHasRole("Master Admin")) && (
                    <Link
                        to={{
                            pathname: "/dashboard",
                        }}
                    >
                        <ListItem button key={"Dashboard"}>
                            <ListItemIcon>
                                <SsidChartIcon style={{ color: "#fe8c03" }} />
                            </ListItemIcon>
                            <ListItemText primary={"Dashboard"} />
                        </ListItem>
                    </Link>
                )}

                {hasManual && (
                    <ListItem button key={"Download Manual"} onClick={handleDownloadManual}>
                        <ListItemIcon>
                            <DescriptionIcon style={{ color: "#fe8c03" }} />
                        </ListItemIcon>
                        <ListItemText primary={"Download Manual"} />
                    </ListItem>
                )}

                {(userHasRole("Client Admin") || userHasRole("Master Admin")) && (
                    <Link
                        to={{
                            pathname: "/user-management",
                        }}
                    >
                        <ListItem button key={"User Management"}>
                            <ListItemIcon>
                                <GroupIcon style={{ color: "#fe8c03" }} />
                            </ListItemIcon>
                            <ListItemText primary={"User Management"} />
                        </ListItem>
                    </Link>
                )}

                {userHasRole("Master Admin") && (
                    <>
                        <ListItemButton onClick={handleCollapseBillingReport}>
                            <ListItemIcon>
                                <IosShareIcon style={{ color: "#fe8c03" }} />
                            </ListItemIcon>
                            <ListItemText primary="Billing Reports" />
                            {openBillingReport ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={openBillingReport} timeout="auto" unmountOnExit>
                            <List component="div" sx={{ pl: 32 }}>
                                <ListItem
                                    button
                                    onClick={(e) => handleModalBillingReport(e, billingReportType.IMAGES_EVENTS)}
                                >
                                    <ListItemIcon>
                                        <PhotoCameraIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Images Events Report" />
                                </ListItem>
                                <ListItem
                                    button
                                    onClick={(e) => handleModalBillingReport(e, billingReportType.CRF_EVENTS)}
                                >
                                    <ListItemIcon>
                                        <PlaylistAddCheckIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary="CRF Events Report" />
                                </ListItem>
                                <ListItem
                                    button
                                    onClick={(e) => handleModalBillingReport(e, billingReportType.USERS_ADDED)}
                                >
                                    <ListItemIcon>
                                        <GroupIcon style={{ color: "#fe8c03" }} />
                                    </ListItemIcon>
                                    <ListItemText primary={"Users Added Report"} />
                                </ListItem>
                            </List>
                        </Collapse>
                    </>
                )}
                <ComingSoonFeature>
                    <ListItem disabled button key={"Check Notifications"}>
                        <ListItemIcon>
                            <Notifications style={{ color: "#fe8c03" }} />
                        </ListItemIcon>
                        <ListItemText primary={"Check Notifications"} />
                    </ListItem>
                </ComingSoonFeature>
                <ComingSoonFeature>
                    <ListItem disabled button key={"Manage System"}>
                        <ListItemIcon>
                            <Build style={{ color: "#fe8c03" }} />
                        </ListItemIcon>
                        <ListItemText primary={"Manage System"} />
                    </ListItem>
                </ComingSoonFeature>
                <ComingSoonFeature>
                    <ListItem disabled button key={"Edit configration and profile"}>
                        <ListItemIcon>
                            <Favorite style={{ color: "#fe8c03" }} />
                        </ListItemIcon>
                        <ListItemText primary={"Edit configration and profile"} />
                    </ListItem>
                </ComingSoonFeature>
            </List>

            <Dialog open={openCompletionCensus} onClose={() => handleCloseCompletionCensus()}>
                <DialogTitle>Download Completion Census</DialogTitle>
                <DialogContent>
                    <DisplayText type="bodyMedium" text={"From"} />
                    <DesktopDatePicker
                        value={CompletionCensusDate.from}
                        onChange={(newDate: Date | null) => handleChangeCompletionCensusDate(newDate, "from")}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                autoFocus
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                }}
                            />
                        )}
                    />
                    <DisplayText type="bodyMedium" text={"To"} />
                    <DesktopDatePicker
                        value={CompletionCensusDate.to}
                        onChange={(newDate: Date | null) => handleChangeCompletionCensusDate(newDate, "to")}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                autoFocus
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                }}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseCompletionCensus()} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={() => handleExportCompletionCensus()} variant="contained">
                        {CompletionCensusDate.to && CompletionCensusDate.from ? "Download" : "Download Full"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openModalBasicAuditReport} onClose={() => handleCloseBasicAuditReport()}>
                <DialogTitle>Download Basic Audit Report</DialogTitle>
                <DialogContent>
                    <DisplayText type="bodyMedium" text={"From"} />
                    <DesktopDatePicker
                        value={basicAuditReportDate.from}
                        onChange={(newDate: Date | null) => handleChangeBasicAuditReportDate(newDate, "from")}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                autoFocus
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                }}
                            />
                        )}
                    />
                    <DisplayText type="bodyMedium" text={"To"} style={{ marginTop: "16px" }} />
                    <DesktopDatePicker
                        value={basicAuditReportDate.to}
                        onChange={(newDate: Date | null) => handleChangeBasicAuditReportDate(newDate, "to")}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                autoFocus
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                }}
                            />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseBasicAuditReport()} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={(e) => handleExportBasicAuditReport(e)}
                        variant="contained"
                        disabled={!selectedTrial}
                    >
                        {basicAuditReportDate.to && basicAuditReportDate.from ? "Download" : "Download Full"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openModalBillingReport} onClose={() => handleCloseBillingReport()}>
                <DialogTitle>Download Billing Report</DialogTitle>
                <DialogContent>
                    <DisplayText type="bodyMedium" text={"From"} />
                    <DesktopDatePicker
                        value={billingReportDate.from}
                        onChange={(newDate: Date | null) =>
                            setBillingReportDate((prev) => ({ ...prev, from: newDate ? newDate.toISOString() : "" }))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                autoFocus
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                }}
                            />
                        )}
                    />
                    <DisplayText type="bodyMedium" text={"To"} style={{ marginTop: "16px" }} />
                    <DesktopDatePicker
                        value={billingReportDate.to}
                        onChange={(newDate: Date | null) =>
                            setBillingReportDate((prev) => ({ ...prev, to: newDate ? newDate.toISOString() : "" }))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                autoFocus
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: true,
                                }}
                            />
                        )}
                    />
                    <DisplayText type="bodyMedium" text={"Format"} style={{ marginTop: "16px" }} />
                    <Select
                        fullWidth
                        value={billingReportFormat}
                        onChange={(e) => setBillingReportFormat(e.target.value as "PDF" | "CSV")}
                    >
                        <MenuItem value="PDF">PDF</MenuItem>
                        <MenuItem value="CSV">CSV</MenuItem>
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseBillingReport()} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={(e) =>
                            handleBillingReports(
                                e,
                                billingReport,
                                billingReportDate.from,
                                billingReportDate.to,
                                billingReportFormat
                            )
                        }
                        variant="contained"
                    >
                        Download
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openModalUsersReport} onClose={() => handleCloseUsersReport()}>
                <DialogTitle>Download Users Report</DialogTitle>
                <DialogContent>
                    <DisplayText type="bodyMedium" text={"Format"} style={{ marginTop: "16px" }} />
                    <Select
                        fullWidth
                        value={usersReportFormat}
                        onChange={(e) => setUsersReportFormat(e.target.value as "PDF" | "CSV")}
                    >
                        <MenuItem value="PDF">PDF</MenuItem>
                        <MenuItem value="CSV">CSV</MenuItem>
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseUsersReport()} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={(e) => handleExportUsersData(e, usersReportFormat)}
                        variant="contained"
                        disabled={!selectedTrial}
                    >
                        Download
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openModalAllUsersReport} onClose={() => handleCloseAllUsersReport()}>
                <DialogTitle>Download All Users Report</DialogTitle>
                <DialogContent>
                    <DisplayText type="bodyMedium" text={"Format"} style={{ marginTop: "16px" }} />
                    <Select
                        fullWidth
                        value={allUsersReportFormat}
                        onChange={(e) => setAllUsersReportFormat(e.target.value as "PDF" | "CSV")}
                    >
                        <MenuItem value="PDF">PDF</MenuItem>
                        <MenuItem value="CSV">CSV</MenuItem>
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseAllUsersReport()} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={(e) => handleExportAllUsersData(e, allUsersReportFormat)} variant="contained">
                        Download
                    </Button>
                </DialogActions>
            </Dialog>

            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "end",
                    p: 5,
                }}
            >
                <DisplayText type="bodyXSmall" text="v1.5.17" />
                <DisplayText type="bodyXSmall" text="v1.18.0" />
            </Box>
        </Box>
    );
};

export default Menu;
